import * as React from "react";
import { useState } from "react";
import { Switch } from "react-router-dom";
import { AmplitudeSession } from "~/analytics/Amplitude";
import type { AnalyticSession } from "~/analytics/AnalyticSession";
import { AnalyticSessionProvider, useAnalyticSession } from "~/analytics/AnalyticSession";
import AuthenticationLayout from "~/areas/authentication/AuthenticationLayout";
import Register from "~/areas/authentication/Register";
import SignIn from "~/areas/authentication/SignIn";
import SignOut from "~/areas/authentication/SignOut";
import type { UserResource } from "~/client/resources";
import { OctopusError } from "~/client/resources";
import { AuthenticationError } from "~/client/resources/authenticationError";
import { client, repository, session } from "~/clientInstance";
import ReloadableRoute from "~/components/ReloadableRoute";
import SecureRoute from "~/components/SecureRoute";
import { StandardLayout } from "~/components/StandardLayout";
import { useThemePaletteType } from "~/components/Theme/useThemePaletteType";
import routeLinks from "~/routeLinks";

type SessionStartEvent = (user: UserResource) => Promise<void>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function AuthenticationRoutes(props: React.ComponentProps<any>) {
    const fallbackAnalyticSession = useAnalyticSession();
    const theme = useThemePaletteType();
    const [analyticsSession, setAnalyticsSession] = useState<AnalyticSession | undefined>(undefined);

    const onSessionStart: SessionStartEvent = async (user) => {
        try {
            const [featureConfigurations, featureToggles] = await Promise.all([repository.FeaturesConfiguration.get(), repository.ServerConfiguration.enabledFeatureToggles()]);
            session.start(user, featureConfigurations, featureToggles);
        } catch (error) {
            throw getSessionStartAuthenticationError(error);
        }

        if (analyticsSession === undefined) {
            const serverInfo = client.tryGetServerInformation();
            if (serverInfo) {
                const session = new AmplitudeSession(serverInfo.installationId, user.Id, theme);
                setAnalyticsSession(session);
                // session.track("Sign In", {});
            }
        }
    };

    const onSessionEnd = () => {
        analyticsSession?.track("Sign Out", {});
        analyticsSession?.end();
        setAnalyticsSession(undefined);
    };

    return (
        <AnalyticSessionProvider session={analyticsSession ?? fallbackAnalyticSession}>
            <Switch>
                <ReloadableRoute
                    path={routeLinks.currentUser.signIn}
                    render={(props) => (
                        <AuthenticationLayout>
                            <SignIn {...props} onSessionStarted={onSessionStart} />
                        </AuthenticationLayout>
                    )}
                />
                <ReloadableRoute
                    path={routeLinks.currentUser.signOut}
                    render={(props) => (
                        <AuthenticationLayout>
                            <SignOut {...props} onSessionEnded={onSessionEnd} />
                        </AuthenticationLayout>
                    )}
                />
                <ReloadableRoute
                    path={routeLinks.currentUser.register(":inviteCode")}
                    render={(props) => (
                        <AuthenticationLayout>
                            <Register {...props} onSessionStarted={onSessionStart} />
                        </AuthenticationLayout>
                    )}
                />
                <SecureRoute path={routeLinks.root} component={StandardLayout} analyticsSession={analyticsSession} />
            </Switch>
        </AnalyticSessionProvider>
    );
}

function getSessionStartAuthenticationError(error: unknown): AuthenticationError {
    let reason = "There was an unknown error";

    if (error instanceof OctopusError) {
        reason = error.StatusCode === 401 ? "This can happen if the Octopus authentication cookie is blocked." : `There was a problem communicating with the Octopus Server: ${error.ErrorMessage}`;
    } else if (error instanceof Error) {
        reason = `There was an unknown error: ${error.message}`;
    }

    const message = "The sign in succeeded but we failed to get the resultant permissions for this user account. ";
    return new AuthenticationError(message + reason);
}
